<template>
  <b-row
    class="step metric"
    :class="{ 'disabled-row': !plan.groupId, 'invalid-fields': invalidFields }"
  >
    <header class="col col-md-9 flex">
      <div class="number">2</div>
      <span class="text-primary medium d-grid flex-grow">
        <span>
          {{ labels.metricHeader }}
          <b>{{ plan.metric.label }}</b>
        </span>
      </span>
      <b-button
        v-if="!isEdit && plan.metric._id"
        class="btn toogle"
        variant="outline-link"
        @click="metricToggle = !metricToggle"
      >
        <i
          class="material-icons"
          :class="{
            'material-icons-unfold-more': !metricOpen,
            'material-icons-unfold-less': metricOpen,
          }"
          >{{ metricOpen ? 'unfold_less' : 'unfold_more' }}</i
        >
      </b-button>
    </header>
    <div v-if="loading" class="loading col col-md-9">
      <loading-state />
      <p class="m-0">
        {{ labels.loading }}
      </p>
    </div>
    <b-col md="9" class="mt-3" v-if="emptyScoreMetrics && !loading">
      <p class="text-quartenary">
        <i class="material-icons">
          info
        </i>
        {{ labels.emptyScoreMetrics | parse }}
      </p>
    </b-col>
    <!-- Metric Option List -->
    <transition name="fade">
      <div
        id="metrics"
        v-if="!loading && !emptyScoreMetrics && metricOpen"
        class="metrics-wrap col col-12"
      >
        <ul class="metric-list p-3">
          <li v-for="metric in optionMetrics" :key="metric._id">
            <label
              class="metric-item-list clickable"
              tabindex="0"
              @click="$emit('setSelectedMetric', metric)"
              @keyup.enter="$emit('setSelectedMetric', metric)"
            >
              <!-- <div class="flex"> -->
              <!-- <span class="flex-grow">{{metric.name}}</span> -->
              <!-- </div> -->
              <div
                class="check-item"
                :class="{ checked: metric._id === plan.metric._id }"
              ></div>
              <metric-item :metric="metric" :canViewMetricsInfo="true" />
              <div class="toggle-check"></div>
            </label>
          </li>
        </ul>
      </div>
    </transition>
    <!-- END - Metric Option List -->
  </b-row>
</template>

<script>
import debounce from '@/helpers/debounce'
import MetricItem from '@/components/Reports/MetricItem.vue'

export default {
  components: {
    MetricItem,
    'loading-state': () => import('@/components/Shared/Loading.vue'),
  },
  props: {
    loading: {
      type: Boolean,
    },
    isEdit: {
      type: Boolean,
    },
    plan: {
      type: Object,
      required: true,
    },
    metrics: {
      type: Array,
      required: true,
    },
    invalidFields: {
      type: Boolean,
    },
  },
  data() {
    return {
      metricToggle: false,
    }
  },
  computed: {
    labels() {
      const form = this.$store.getters.resources.component.actionPlan
        .formMetrics
      const metricHeader = this.plan.metric._id
        ? form.metricSelected
        : form.addMetric
      return {
        metricHeader,
        ...form,
      }
    },
    metricOpen: {
      set() {},
      get() {
        if (!this.plan.groupId) return false

        if (!this.plan.metric._id) return true

        return !!this.metricToggle
      },
    },
    optionMetrics() {
      const simpleMetric = ({ id, label, score, scoreLabel }) => ({
        id,
        _id: id,
        score,
        scoreLabel,
        label,
        noBorder: true,
      })
      return (this.metrics || []).map(simpleMetric)
    },
    emptyScoreMetrics() {
      return (
        this.metrics.length && this.metrics.every(({ score }) => isNaN(score))
      )
    },
  },
  watch: {
    'plan.metric'(value) {
      debounce(200, () => {
        this.metricToggle = !value._id
      })()
    },
    'plan.groupId'(value) {
      if (!value) this.$emit('setSelectedMetric', {})
    },
  },
}
</script>

<style lang="scss">
.metric-item-list {
  .wrapper {
    margin: 0 !important;
    min-width: initial;
    width: 100%;
  }

  .metric-info {
    padding: 0 !important;

    h4 {
      font-size: 13px !important;
      padding-right: 16px;
    }

    .bar {
      margin-top: 3px !important;

      .label {
        font-size: 25px !important;
      }
    }

    @media (max-width: 1375px) {
      padding: 0 !important;
      padding-right: 16px !important;
    }
  }
}
</style>

<style lang="scss" scoped>
@import '~@/styles/custom/colors';
@import '~@/styles/custom/mixins';
@import '~bootstrap/scss/_functions';
@import '~bootstrap/scss/_variables';
@import '~bootstrap/scss/mixins/breakpoints';

$metricItemBorderColor: map-get($theme-colors, 'gray01');

@keyframes borderError {
  0% {
    border-color: $metricItemBorderColor;
  }

  100% {
    border-color: map-get($theme-colors, 'secondary');
    box-shadow: 0px 0px 2px map-get($theme-colors, 'secondary');
  }
}

.invalid-fields /deep/ .metric-item-list {
  @include animation(borderError 0.2s linear 0s forwards);
}

.loading {
  margin: 0 auto;
  text-align: center;
  font-size: 13px;

  /deep/ .lds-ellipsis {
    height: 20px;

    div {
      height: 8px;
      top: 10px;
      width: 8px;
    }
  }
}

.toogle {
  align-items: center;
  background: map-get($theme-colors, 'gray08');
  border: 1px solid map-get($theme-colors, 'gray01');
  display: flex;
  justify-content: center;
  max-height: 36px;
  width: 36px;
}

.metrics-wrap {
  max-width: 75%;
  padding: 0px;
}

.metric-list {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  margin: 0px;

  li {
    list-style: none;
    padding: 0.3rem;
    width: 33%;

    @include media-breakpoint-down(sm) {
      width: 100%;
    }

    .metric-item-list {
      border: 1px solid $metricItemBorderColor;
      border-radius: 8px;
      display: inline-block;
      height: 100%;
      margin-bottom: 0;
      padding-top: 0;
      position: relative;
      width: 100%;

      .check-item {
        align-items: center;
        border: 2px solid $metricItemBorderColor;
        border-radius: 14px;
        display: inline-flex;
        justify-content: center;
        position: absolute;
        top: 7px;
        right: 10px;
        height: 20px;
        width: 20px;

        &.checked {
          &::before {
            content: ' ';
            background: map-get($theme-colors, 'lima');
            border-radius: 12px;
            display: inline-block;
            height: 12px;
            width: 12px;
          }
        }
      }
    }
  }
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
</style>
